import React, { useContext, useEffect, useState } from 'react';
import ButtonCancelForm from '../../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import { FundRequestStatus, FundsRequestDataForm } from '../../../../app/models/Fund';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import useAuthService from '../../../../app/services/hooks/useAuthService';
import useBankAccountService from '../../../../app/services/hooks/useBankAccountService';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import { AppContext } from '../../../../contexts/AppContext';
import { BankAccount } from '../../../../app/models/BankAccountType';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';


interface Props {
    status: FundRequestStatus;
    setStatus: (status: FundRequestStatus) => void;
    fundsRequest?: FundsRequestDataForm;
    errorFields?: any;
    onSaved?: (status?: FundRequestStatus) => void;
    onCancel?: () => void;
    onError?: () => void;
}

const IssueDateForm = ({ 
    status, 
    setStatus, 
    fundsRequest,
    onSaved, 
    onCancel, 
    onError, 
    errorFields 
}: Props) => {
    
    if (!status) return null;

    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();
    const { getProfile } = useAuthService();
    const { getBankAccountsBySociety } = useBankAccountService();
    const { showLoading, hideLoading } = useContext(AppContext);
    
    const [userProfile, setUserProfile] = useState<any>(null);
    const [societyBankAccounts, setSocietyBankAccounts] = useState<BankAccount[]>([]);

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    useEffect(() => {
        // Obtener perfil del usuario
        if (showLoading) showLoading('loading', 'Cargando datos...');
        getProfile({
            onSuccess: (response: ServiceResponse) => {
                setUserProfile(response.data.profile);
                
                // Usar la sociedad de la solicitud de fondos si está disponible,
                // o del status en caso de cambio masivo
                const societyId = fundsRequest?.society_id || status?.society_id;
                
                // Cargar cuentas de la sociedad
                if (societyId) {
                    getBankAccountsBySociety(societyId, {
                        onSuccess: (bankResponse: ServiceResponse) => {
                            console.log('Bank accounts response:', bankResponse.data);
                            setSocietyBankAccounts(bankResponse.data.bank_accounts || []);
                            if (hideLoading) hideLoading();
                        },
                        onError: (error: ServiceResponse) => {
                            console.error('Error loading bank accounts:', error);
                            if (hideLoading) hideLoading();
                        }
                    });
                } else {
                    console.log('No society_id available');
                    if (hideLoading) hideLoading();
                }
            },
            onError: () => {
                if (hideLoading) hideLoading();
            }
        });
    }, [fundsRequest?.society_id, status?.society_id]);

    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = event.target;
        
        // Convertir society_bank_account_id a número
        const processedValue = name === 'society_bank_account_id' ? (value ? Number(value) : undefined) : value;
        
        console.log('Field changed:', name, '=', processedValue);
        setStatus({ ...status, [name]: processedValue });
    };

    const save = () => {
        console.log('Saving with status:', status);
        
        // Validación: verificar que se haya seleccionado una cuenta
        if (!status?.society_bank_account_id) {
            useReactConfirmAlert().errorAlert({
                title: 'Error',
                message: 'Debe seleccionar una cuenta corriente de origen'
            });
            return;
        }
        
        if (onSaved) onSaved(status);
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return (
        <>
            <div className="mb-2 row">
                <div className="col-md-6">
                    <label className="col-form-label">Sociedad</label>
                    <input
                        type="text"
                        className="form-control"
                        value={fundsRequest?.society || status?.society || 'Cargando...'}
                        disabled
                    />
                </div>
                <div className="col-md-6">
                    <label className="col-form-label">Usuario que confirma</label>
                    <input
                        type="text"
                        className="form-control"
                        value={userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : 'Cargando...'}
                        disabled
                    />
                </div>
            </div>
            <div className="mb-2 row">
                <div className="col-md-6">
                    <label className="col-form-label">Cuenta corriente origen *</label>
                    <select
                        name="society_bank_account_id"
                        className={`form-select ${fieldHasError('society_bank_account_id')}`}
                        onChange={handleChange}
                        value={status?.society_bank_account_id || ''}
                        onFocus={() => onFocusRemove('society_bank_account_id')}
                    >
                        <option value="">Seleccione cuenta</option>
                        {societyBankAccounts.length > 0 ? (
                            societyBankAccounts.map((account: BankAccount) => (
                                <option key={account.id} value={account.id}>
                                    {account.bank?.name || 'Sin banco'} - {account.account_number || 'Sin número'} ({account.account_type?.name || 'Sin tipo'})
                                </option>
                            ))
                        ) : (
                            <option value="" disabled>No hay cuentas disponibles</option>
                        )}
                    </select>
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('society_bank_account_id')}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="col-form-label">Fecha de emisión *</label>
                    <input
                        type="date"
                        name="issue_date"
                        className={`form-control ${fieldHasError('issue_date')}`}
                        onChange={handleChange}                        
                        value={status?.issue_date || ''}
                        placeholder="Ingrese fecha de emisión"
                        onFocus={() => onFocusRemove('issue_date')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('issue_date')}
                    </div>
                </div>
            </div>
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={save} />
                </div>
            </div>
        </>
    );
};

export default IssueDateForm;
